import {type HousingProduct, HousingProductType, type KITProductData} from "../../../../../../models/ProductData";
import ProductDesignation from "../../../../../../ProductDetails/components/ProductDesignation";
import {Link, LinkType} from "../../../../../../common/components/Link";
import {type CSSProperties, type ReactNode, useMemo} from "react";
import ProductImage, {type ProductImageWidths} from "../../../../../../common/components/Product/ProductImage";
import {Translation} from "../../../../../../common/Translation";
import Parameter from "../../../../../../common/components/Parameter";
import classNames from "classnames";
import {Card} from "../../../../../../Configurators/Linear/components/Card";
import {useBooleanState} from "../../../../../../common/hooks/useBooleanState";
import {QuantitySubtotal} from "../../../../commonParts";
import {ToggleIcon} from "../../../../../../common/components/icon/ToggleIcon";
import {Collapse} from "~/common/components/Collapse";
import {IconButton, styled} from "@mui/material";
import {FontSize, getFontStyle} from "../../../../../../common/components/materialInput/Fonts";
import {useTranslation} from "react-i18next";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

interface IncludedPartsSectionProps {
  descriptor: ProductDescriptor<KITProductData>;
  kitQty?: number;
  editMode?: boolean;
}

export const IncludedPartsSection = ({ descriptor, kitQty = 0, editMode }: IncludedPartsSectionProps) => {
  const { val: isExpanded, toggle } = useBooleanState(!editMode);
  const { t } = useTranslation();

  const totalQty = descriptor.base.includedParts.reduce((sum, part) => sum + part.quantity * kitQty, 0);

  return (
    <div className={"scha__cart__included-parts-section"}>
      <Card data-test-id={`included-parts`} disableHover={true} className={"scha__cart__product-table__included-parts"}>
        <PartsTitle className={"scha__cart__product-table__included-parts__title"}>
          {t("page.cart.housing.included.title")}
        </PartsTitle>
        <QuantitySubtotal className={"scha__cart__product-table__included-parts__quantity"} quantity={totalQty} />
        {editMode && (
          <IconButton
            className={"scha__cart__product-table__included-parts__toggle"}
            onClick={toggle}
            data-test-id={`included-parts-toggle`}
          >
            <ToggleIcon isActive={isExpanded} />
          </IconButton>
        )}
      </Card>
      <Collapse in={isExpanded} className={"scha__cart__included-parts-section__item-list"}>
        {descriptor.base.includedParts.map((housingProduct) => (
          <IncludedPartItem
            key={housingProduct.id}
            className={"scha__cart__included-parts-section__item"}
            kitQuantity={kitQty}
            housingProduct={housingProduct}
          />
        ))}
      </Collapse>
    </div>
  );
};
export const PartsTitle = styled("p")({
  margin: 0,
  marginBottom: 20,
  ...getFontStyle(FontSize.TitleMedium),
});

interface PartItemFrameProps {
  className?: string;
  upper?: ReactNode;
  lower?: ReactNode;
  upperCls?: string;
  lowerCls?: string;
}

export const PartItemFrame = ({ upper, lower, upperCls, lowerCls, className }: PartItemFrameProps) => {
  return (
    <div className={classNames("scha__cart__product-part-frame", className)}>
      <Card
        variant={"square"}
        className={classNames("scha__cart__product-part-frame__upper", upperCls)}
        disableHover={true}
      >
        {upper}
      </Card>
      <Card
        variant={"square"}
        className={classNames("scha__cart__product-part-frame__lower", lowerCls)}
        disableHover={true}
      >
        {lower}
      </Card>
    </div>
  );
};

interface ItemProps {
  kitQuantity: number;
  housingProduct: HousingProduct;
  className?: string;
}

const IncludedPartItem = ({ housingProduct, className, kitQuantity }: ItemProps) => {
  const { t } = useTranslation();

  const product = housingProduct.product;
  const isHousing = housingProduct.type === HousingProductType.HOUSING;

  let designation = (
    <ProductDesignation
      size={"title-medium"}
      className={"scha__cart__included-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
    />
  );

  if (!isHousing && product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const quantity = housingProduct.quantity * kitQuantity;

  const style: CSSProperties = useMemo(() => {
    return {
      visibility: isHousing ? "hidden" : "visible",
    };
  }, [isHousing]);

  return (
    <PartItemFrame
      className={className}
      upper={
        <div className={"d-flex row"}>
          <div className="col-xs-3">
            <ProductImage product={product} widths={IMAGE_WIDTHS} />
          </div>
          <div className="col-xs-9">
            {designation}
            <p className="scha__cart__included-parts-item__short-description" style={style}>
              <Translation {...product.shortDescription} />
            </p>
          </div>
        </div>
      }
      lower={<Parameter label={t("input.quantity")} value={quantity} />}
    />
  );
};
const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};
